/**
 * CR-7482: the one way to offer a stored file for download in this app.
 *
 * Two patterns that look reasonable are both broken, and this component exists
 * so neither gets written again:
 *
 * 1. `<a href={rawStorageUrl}>` — on S3 that path is a presigned URL valid for
 *    300 seconds (`AWS_QUERYSTRING_EXPIRE`), minted when the API response was
 *    serialized rather than when the user clicks. A few minutes later S3
 *    rejects it and returns its error document, which the browser renders as
 *    "This XML file does not appear to have any style information associated
 *    with it." That was the original CR-7482 report.
 *
 * 2. `<a href={apiDownloadUrl}>` — plain browser navigation sends no headers,
 *    and this app authenticates with a token, so DRF answers 401. The same
 *    trap is documented on `reportingAPI.downloadDashboardExport`.
 *
 * The file has to be fetched through the axios client (whose interceptor
 * attaches the token — including for absolute URLs, where axios bypasses
 * `baseURL`) and handed to the browser as a blob.
 */
import { useState, type ReactNode } from 'react';
import { Loader2 } from 'lucide-react';
import { toast } from 'sonner';

import api from '../../lib/api/client';

interface FileDownloadLinkProps {
  /** Absolute URL of the authenticated download endpoint (`download_url`). */
  url: string;
  /** Filename used if the response carries no Content-Disposition. */
  fallbackName?: string;
  /** Leading icon; replaced by a spinner while fetching. */
  icon?: ReactNode;
  /** Label. Omit for an icon-only control. */
  children?: ReactNode;
  className?: string;
  title?: string;
  'aria-label'?: string;
}

/** Pull the server's filename out of Content-Disposition, if it sent one. */
function filenameFrom(disposition: string | undefined, fallback: string): string {
  const match = /filename="?([^";]+)"?/i.exec(disposition || '');
  return match?.[1] || fallback;
}

/**
 * A button that fetches a file through the authenticated API and opens it.
 *
 * @param url - Absolute download endpoint, as the serializer's `download_url`.
 * @param fallbackName - Name to save under if the response sets no filename.
 * @returns A button rendering `icon` + `children`, with a spinner while loading.
 */
export default function FileDownloadLink({
  url, fallbackName = 'document', icon, children, className, title,
  'aria-label': ariaLabel,
}: FileDownloadLinkProps) {
  const [loading, setLoading] = useState(false);

  const fetchAndOpen = async () => {
    setLoading(true);
    try {
      const res = await api.get(url, { responseType: 'blob' });
      const blob = res.data as Blob;
      const name = filenameFrom(
        res.headers['content-disposition'] as string | undefined,
        fallbackName,
      );
      const objectUrl = URL.createObjectURL(blob);

      // Prefer a new tab so PDFs and images preview inline, as the old links
      // did. If the popup is blocked, save the file rather than failing.
      const opened = window.open(objectUrl, '_blank');
      if (!opened) {
        const anchor = document.createElement('a');
        anchor.href = objectUrl;
        anchor.download = name;
        document.body.appendChild(anchor);
        anchor.click();
        anchor.remove();
      }
      // Revoke late: too early and the new tab never finishes loading.
      window.setTimeout(() => URL.revokeObjectURL(objectUrl), 60000);
    } catch (err: unknown) {
      const message = err instanceof Error ? err.message : 'Unknown error';
      console.error('FileDownloadLink: download failed', message);
      toast.error('The document could not be opened. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <button
      type="button"
      onClick={fetchAndOpen}
      disabled={loading}
      title={title}
      aria-label={ariaLabel}
      aria-busy={loading}
      className={className}
    >
      {loading ? <Loader2 className="h-3.5 w-3.5 animate-spin shrink-0" /> : icon}
      {children}
    </button>
  );
}
